import { useMutation } from '@tanstack/react-query';
import { AlertTriangle, ArrowRight } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HUBSPOT_CATEGORIES,
  HUBSPOT_SCRATCH_TEMPLATE,
  HUBSPOT_TEMPLATES,
  HubSpotTemplate,
} from './hubspotTemplates.const';
import { QueryKey } from '@/Config/api/queryKey';
import { ActionDto, HttpActionDto, PatchActionDto } from '@/Domain/api/Action/dto/Action.dto';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/Domain/components/Shared/Dialog/Dialog';
import { HubSpotIcon } from '@/Domain/components/icons/HubSpot.icon';
import { HttpMethodEnum } from '@/Domain/enum/HttpMethod.enum';
import { useHubSpotTokenStatus } from '@/Domain/hooks/Integration/useHubSpotTokenStatus.hook';
import { MODAL_NAMES, useModalStore } from '@/Domain/store/modal.store';

const HttpMethodBadge = ({ method }: { method: HttpMethodEnum }) => {
  const colorMap: Record<string, string> = {
    [HttpMethodEnum.POST]: 'bg-emerald-100 text-emerald-700',
    [HttpMethodEnum.PATCH]: 'bg-amber-100 text-amber-700',
    [HttpMethodEnum.PUT]: 'bg-blue-100 text-blue-700',
    [HttpMethodEnum.GET]: 'bg-purple-100 text-purple-700',
    [HttpMethodEnum.DELETE]: 'bg-red-100 text-red-700',
  };

  return (
    <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${colorMap[method] ?? ''}`}>
      {method}
    </span>
  );
};

type TemplateCardProps = {
  id: string;
  name: string;
  description: string;
  method: HttpMethodEnum;
  isLoading: boolean;
  onClick: () => void;
};

const TemplateCard = ({ id, name, description, method, isLoading, onClick }: TemplateCardProps) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isLoading}
    data-testid={`hubspot-template-card-${id}`}
    className="w-full text-left p-4 rounded-xl border border-border hover:border-border
               hover:shadow-sm transition-all bg-card group disabled:opacity-50 disabled:cursor-not-allowed"
  >
    <div className="flex items-start justify-between">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1">
          <span className="font-medium text-sm text-foreground">{name}</span>
          <HttpMethodBadge method={method} />
        </div>
        <p className="text-xs text-foreground-subtle line-clamp-2">{description}</p>
      </div>
      <ArrowRight
        className="size-4 text-foreground-subtle group-hover:text-foreground-subtle
                   transition-colors mt-0.5 shrink-0 ml-3"
      />
    </div>
  </button>
);

export const HubSpotTemplateGalleryDialog = () => {
  const { isOpen } = useModalStore();

  if (!isOpen(MODAL_NAMES.HUBSPOT_TEMPLATE_GALLERY_DIALOG)) return null;

  return <HubSpotTemplateGalleryDialogContent />;
};

const HubSpotTemplateGalleryDialogContent = () => {
  const { t } = useTranslation('actions_page');
  const { closeModal, toggleModal } = useModalStore();
  const { data: hubSpotStatus } = useHubSpotTokenStatus();
  const [loadingTemplateId, setLoadingTemplateId] = useState<string | null>(null);

  const createHttpRequestActionMutation = useMutation<HttpActionDto, unknown, void>({
    mutationKey: [QueryKey.actions.httpRequest.create],
  });

  const patchActionMutation = useMutation<ActionDto, unknown, PatchActionDto>({
    mutationKey: [QueryKey.actions.basic.patch],
  });

  const hubspotOauthMutation = useMutation<{ url: string }>({
    mutationKey: [QueryKey.integration.hubspotOauthMutation],
    onSuccess: (data) => {
      window.location.href = data.url;
    },
  });

  const handleSelectTemplate = async (template: HubSpotTemplate | null) => {
    if (loadingTemplateId !== null) return;

    const selectedTemplate = template ?? HUBSPOT_SCRATCH_TEMPLATE;
    setLoadingTemplateId(selectedTemplate.id);

    try {
      const httpRequestAction = await createHttpRequestActionMutation.mutateAsync();
      patchActionMutation.mutate({
        id: httpRequestAction.id,
        icon: 'FaHubspot',
        icon_color: '#EF4444',
      });
      toggleModal(MODAL_NAMES.HTTP_REQUEST_ACTION_SHEET, {
        httpRequestAction,
        hubspotTemplate: selectedTemplate,
      });
    } catch {
      setLoadingTemplateId(null);
    }
  };

  const isHubSpotConnected = hubSpotStatus?.has_token;

  return (
    <Dialog open onOpenChange={closeModal}>
      <DialogContent
        className="max-w-2xl max-h-[85vh] overflow-hidden flex flex-col"
        data-testid="hubspot-template-gallery-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <HubSpotIcon className="size-5" />
            {t('hubspot_templates.title')}
          </DialogTitle>
          <DialogDescription>{t('hubspot_templates.description')}</DialogDescription>
        </DialogHeader>

        {!isHubSpotConnected && (
          <div
            className="flex items-center gap-3 p-3 rounded-lg bg-amber-50 border border-amber-200"
            data-testid="hubspot-connection-warning"
          >
            <AlertTriangle className="size-4 text-amber-600 shrink-0" />
            <p className="text-sm text-amber-800 flex-1">
              {t('hubspot_templates.connection_warning')}
            </p>
            <Button
              variant="link"
              className="text-sm text-amber-700 font-medium shrink-0 !p-0 !h-auto"
              onClick={() => hubspotOauthMutation.mutate()}
              disabled={hubspotOauthMutation.isPending}
              data-testid="hubspot-connect-button"
            >
              {t('hubspot_templates.connect_hubspot')}
            </Button>
          </div>
        )}

        <div className="overflow-y-auto flex-1 space-y-6 pr-1 min-h-0">
          {HUBSPOT_CATEGORIES.map((category) => {
            const categoryTemplates = HUBSPOT_TEMPLATES.filter(
              (tpl) => tpl.category === category.id,
            );
            if (categoryTemplates.length === 0) return null;

            return (
              <div key={category.id}>
                <div className="flex items-center gap-2 mb-3">
                  <category.icon className="size-4 text-foreground-subtle" />
                  <h3
                    className="text-sm font-medium text-muted-foreground"
                    data-testid={`hubspot-category-${category.id}`}
                  >
                    {t(category.labelKey)}
                  </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {categoryTemplates.map((template) => (
                    <TemplateCard
                      key={template.id}
                      id={template.id}
                      name={t(template.nameKey)}
                      description={t(template.descriptionKey)}
                      method={template.method}
                      isLoading={loadingTemplateId === template.id}
                      onClick={() => handleSelectTemplate(template)}
                    />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter className="flex flex-row !justify-between items-center border-t pt-4">
          <div className="flex-1">
            <p
              className="text-xs text-foreground-subtle"
              data-testid="hubspot-start-from-scratch-description"
            >
              {t('hubspot_templates.start_from_scratch_description')}
            </p>
          </div>
          <Button
            variant="secondary"
            onClick={() => handleSelectTemplate(null)}
            disabled={loadingTemplateId !== null}
            data-testid="hubspot-start-from-scratch-button"
          >
            {t('hubspot_templates.start_from_scratch')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
